import { useNavigate } from 'react-router-dom';

/**
 * Session summary modal component
 */
export const SessionSummaryModal = ({ sessionSummary, onClose }) => {
  const navigate = useNavigate();

  if (!sessionSummary) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border dark:border-gray-700">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-blue-500 dark:from-emerald-600 dark:to-blue-600 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Assessment Summary</h2>
              <p className="text-sm text-white/80 dark:text-white/90 mt-1">
                {sessionSummary.assessment_type} - {sessionSummary.company_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 dark:hover:bg-white/30 rounded-full p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Participants */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">Consultant</h3>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{sessionSummary.consultant?.name}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{sessionSummary.consultant?.email}</p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-emerald-900 dark:text-emerald-200 mb-2">User</h3>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{sessionSummary.user?.name}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{sessionSummary.user?.email}</p>
            </div>
          </div>

          {/* Industry Context */}
          <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Assessment Context</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Industry</label>
                <p className="font-medium text-gray-900 dark:text-gray-100">{sessionSummary.industry?.name}</p>
              </div>
              {sessionSummary.sub_industry && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Sub-Industry</label>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{sessionSummary.sub_industry.name}</p>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Assessment Type</label>
                <p className="font-medium text-gray-900 dark:text-gray-100">{sessionSummary.assessment_type}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Status</label>
                <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                  {sessionSummary.status}
                </span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Assessment Statistics
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                  {sessionSummary.statistics?.questions_answered || 0}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Questions Answered</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {sessionSummary.statistics?.total_questions || 0}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total Questions</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {sessionSummary.statistics?.completion_percentage || 0}%
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Completion</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {sessionSummary.statistics?.average_confidence ? 
                    `${sessionSummary.statistics.average_confidence}/5` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Avg Confidence</div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <label className="text-gray-500 dark:text-gray-400">Started At</label>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {sessionSummary.started_at ? new Date(sessionSummary.started_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-gray-500 dark:text-gray-400">Completed At</label>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {sessionSummary.ended_at ? new Date(sessionSummary.ended_at).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Report Generation Info */}
          <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 dark:border-blue-400 p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">Report Generation</h4>
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  The assessment data has been saved and is ready for report generation. 
                  The report generation system will use the same flow as self-guided assessments 
                  (payment → PDF generation → email delivery).
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                onClose();
                navigate('/consultant/dashboard');
              }}
              className="flex-1 px-4 py-2 bg-emerald-600 dark:bg-emerald-500 text-white rounded-md hover:bg-emerald-700 dark:hover:bg-emerald-600 font-medium"
            >
              Back to Clients
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

